<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import BaseBubble from './Base.vue';
import { useMessageContext } from '../provider.js';
import { MESSAGE_VARIANTS } from '../constants';

const { content, contentAttributes, variant } = useMessageContext();
const { t } = useI18n();

const interactive = computed(() => contentAttributes.value?.interactive ?? {});

const headerType = computed(() => interactive.value.headerType || '');
const headerText = computed(() => interactive.value.headerText || '');
const headerMediaUrl = computed(() => interactive.value.headerMediaUrl || '');
const buttons = computed(() => interactive.value.buttons || []);

const controlClass = computed(() => {
  if (variant.value === MESSAGE_VARIANTS.BOT) {
    return 'border-[#cbd5e1] bg-white/85 text-[#334155] shadow-sm';
  }

  if (variant.value === MESSAGE_VARIANTS.USER) {
    return 'border-n-strong bg-n-solid-1 text-n-slate-12 shadow-sm dark:bg-n-solid-2';
  }

  if (variant.value === MESSAGE_VARIANTS.PRIVATE) {
    return 'border-n-amber-8 bg-n-solid-1/70 text-n-amber-12 shadow-sm';
  }

  return 'border-white/30 bg-white/15 text-white shadow-sm';
});

const controlIconClass = computed(() => {
  if (variant.value === MESSAGE_VARIANTS.BOT) {
    return 'bg-[#eef2ff] text-n-brand';
  }

  if (variant.value === MESSAGE_VARIANTS.USER) {
    return 'bg-n-brand/10 text-n-brand';
  }

  if (variant.value === MESSAGE_VARIANTS.PRIVATE) {
    return 'bg-n-amber-4 text-n-amber-11';
  }

  return 'bg-white/15 text-current';
});

const action = computed(() => {
  const type = interactive.value.type;
  if (type === 'cta_url') {
    return {
      icon: 'i-lucide-external-link',
      label: interactive.value.ctaDisplayText,
      url: interactive.value.ctaUrl,
    };
  }
  if (type === 'location_request_message') {
    return {
      icon: 'i-lucide-map-pin',
      label: t('CONVERSATION.INTERACTIVE.PREVIEW.SEND_LOCATION'),
    };
  }
  if (type === 'flow') {
    return {
      icon: 'i-lucide-list-checks',
      label: interactive.value.flowCta,
    };
  }
  if (type === 'catalog_message') {
    return {
      icon: 'i-lucide-shopping-bag',
      label: t('CONVERSATION.INTERACTIVE.PREVIEW.VIEW_CATALOG'),
    };
  }
  return null;
});
</script>

<template>
  <BaseBubble class="overflow-hidden" data-bubble-name="whatsapp-interactive">
    <div v-if="headerType === 'text'" class="px-4 pt-3 text-sm font-semibold">
      {{ headerText }}
    </div>
    <img
      v-else-if="headerType === 'image' && headerMediaUrl"
      :src="headerMediaUrl"
      :alt="t('CONVERSATION.INTERACTIVE.HEADER_TYPES.IMAGE')"
      class="max-h-64 w-full object-cover"
    />
    <video
      v-else-if="headerType === 'video' && headerMediaUrl"
      :src="headerMediaUrl"
      class="max-h-64 w-full bg-black object-contain"
      controls
      preload="metadata"
    />
    <a
      v-else-if="headerType === 'document' && headerMediaUrl"
      :href="headerMediaUrl"
      target="_blank"
      rel="noopener noreferrer"
      class="mx-3 mt-3 flex items-center gap-2 rounded-lg border border-current/20 bg-white/10 px-3 py-2 text-current"
    >
      <span class="i-lucide-file-text size-4 shrink-0" />
      <span class="min-w-0 truncate text-sm font-medium">
        {{
          interactive.headerFilename ||
          t('CONVERSATION.INTERACTIVE.HEADER_TYPES.DOCUMENT')
        }}
      </span>
    </a>

    <div class="px-4 py-3">
      <div v-dompurify-html="content" class="prose prose-bubble text-sm" />
      <p v-if="interactive.footerText" class="mb-0 mt-2 text-xs opacity-70">
        {{ interactive.footerText }}
      </p>
    </div>

    <div
      v-if="buttons.length"
      class="flex flex-col gap-2 border-t-[0.5px] border-n-weak/50 px-3 pb-3 pt-2.5"
    >
      <div
        v-for="button in buttons"
        :key="button.id"
        class="flex min-h-11 cursor-default items-center gap-2.5 rounded-xl border px-2.5 py-2 text-sm font-semibold"
        :class="controlClass"
      >
        <span
          class="grid size-7 shrink-0 place-content-center rounded-lg"
          :class="controlIconClass"
        >
          <span class="i-lucide-mouse-pointer-click size-3.5" />
        </span>
        <span class="min-w-0 flex-1 text-left leading-5">
          {{ button.title }}
        </span>
        <span class="i-lucide-chevron-right size-4 shrink-0 opacity-55" />
      </div>
    </div>

    <component
      :is="action?.url ? 'a' : 'div'"
      v-else-if="action"
      :href="action.url || undefined"
      :target="action.url ? '_blank' : undefined"
      :rel="action.url ? 'noopener noreferrer' : undefined"
      class="m-3 flex min-h-11 items-center gap-2.5 rounded-xl border px-2.5 py-2 text-sm font-semibold"
      :class="controlClass"
    >
      <span
        class="grid size-7 shrink-0 place-content-center rounded-lg"
        :class="controlIconClass"
      >
        <span :class="action.icon" class="size-3.5" />
      </span>
      <span class="min-w-0 flex-1 text-left leading-5">
        {{ action.label }}
      </span>
      <span class="i-lucide-chevron-right size-4 shrink-0 opacity-55" />
    </component>
  </BaseBubble>
</template>
